import { useModuleData } from "@/hooks/useModuleData";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { KpiCard } from "@/components/common/KpiCard";
import { Loader } from "@/components/common/Loader";
import { shortDate } from "@/lib/format";
import api from "@/lib/api";
import { Network, ArrowDownLeft, ArrowUpRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function Edi() {
  const { data: msgs, loading, reload } = useModuleData("/edi-messages");
  if (loading || !msgs) return <Loader />;

  const inbound = msgs.filter((m) => m.direction === "INBOUND").length;
  const outbound = msgs.filter((m) => m.direction === "OUTBOUND").length;
  const errors = msgs.filter((m) => m.status === "ERROR").length;
  const processed = msgs.filter((m) => m.status === "PROCESSED").length;

  const reprocess = async (m) => {
    await api.post(`/edi-messages/${m.msg_id}/reprocess`);
    toast.success(`EDI ${m.doc_type} ${m.msg_id} reprocessed`);
    reload();
  };

  const columns = [
    { key: "msg_id", label: "Message", render: (r) => <span className="font-mono text-xs text-[#0284C7]">{r.msg_id}</span> },
    { key: "doc_type", label: "Doc", render: (r) => (
      <div><div className="font-mono text-sm text-[#32363a]">{r.doc_type}</div><div className="text-[10px] text-[#6a6d70]">{r.description}</div></div>) },
    { key: "direction", label: "Direction", render: (r) => (
      <span className={`inline-flex items-center gap-1 font-mono text-[11px] ${r.direction === "INBOUND" ? "text-cyan-600" : "text-violet-600"}`}>
        {r.direction === "INBOUND" ? <ArrowDownLeft size={13} /> : <ArrowUpRight size={13} />}{r.direction}</span>) },
    { key: "standard", label: "Standard", render: (r) => <span className="font-mono text-[10px] text-[#6a6d70]">{r.standard}</span> },
    { key: "partner", label: "Partner" },
    { key: "timestamp", label: "Time", render: (r) => shortDate(r.timestamp) },
    { key: "status", label: "Status", badge: true },
    { key: "actions", label: "", render: (r) => (r.status === "ERROR" ? (
      <button data-testid={`edi-reprocess-${r.msg_id}`} onClick={() => reprocess(r)}
        className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-[#eaedf2] px-2 py-1 text-[11px] hover:border-[#0284C7]"><RefreshCw size={13} /> Reprocess</button>) : null) },
  ];

  return (
    <div>
      <PageHeader title="EDI Gateway" subtitle="ANSI X12 / EDIFACT partner messages — 850 PO, 855 ACK, 856 ASN, 810 Invoice, 940 ship orders with transmission logs." icon={Network} accent="#0284C7" />
      <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Inbound" value={inbound} accent="#06B6D4" icon={ArrowDownLeft} />
        <KpiCard label="Outbound" value={outbound} accent="#8B5CF6" icon={ArrowUpRight} />
        <KpiCard label="Processed" value={processed} accent="#10B981" />
        <KpiCard label="Errors" value={errors} accent="#EF4444" />
      </div>
      <DataTable testId="edi-messages-table" columns={columns} rows={msgs} />
    </div>
  );
}
